import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, Mail, Lock, User as UserIcon, FileText } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useState } from 'react';

export const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [fiscalCode, setFiscalCode] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(email, password, fullName, fiscalCode || undefined);
      toast.success('Registrazione completata! Effettua il login.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Register Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Logo and Title */}
          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-md bg-slate-900 mb-4">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <img
              src="/assets/artego-logo.svg"
              alt="Artego Logo"
              className="h-12 w-auto mx-auto"
              data-testid="register-logo"
            />
            <h1 className="font-heading text-4xl font-bold text-slate-900 tracking-tight">
              Registrati
            </h1>
            <p className="text-base text-stone-600">
              Crea il tuo account Artego
            </p>
          </div>

          {/* Register Form */}
          <form onSubmit={handleRegister} className="space-y-6" data-testid="register-form">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName" className="text-sm font-medium text-slate-900">
                  Nome Completo *
                </Label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="Mario Rossi"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="pl-10"
                    data-testid="fullname-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-900">
                  Email *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tuo@email.it"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="email-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-900">
                  Password *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    data-testid="password-input"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fiscalCode" className="text-sm font-medium text-slate-900">
                  Codice Fiscale (opzionale)
                </Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-stone-400" />
                  <Input
                    id="fiscalCode"
                    type="text"
                    placeholder="RSSMRA80A01H501U"
                    value={fiscalCode}
                    onChange={(e) => setFiscalCode(e.target.value.toUpperCase())}
                    className="pl-10"
                    data-testid="fiscal-code-input"
                    maxLength={16}
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-slate-900 text-white hover:bg-slate-800 shadow-sm rounded-md py-3 font-medium transition-all active:scale-95"
              data-testid="register-submit-button"
              disabled={loading}
            >
              {loading ? 'Registrazione...' : 'Registrati'}
            </Button>
          </form>

          {/* Footer */}
          <div className="text-center">
            <p className="text-sm text-stone-600">
              Hai già un account?{' '}
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="font-medium text-orange-800 hover:text-orange-900"
                data-testid="login-link"
              >
                Accedi
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Image (Hidden on mobile) */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1712763505655-1077b86cea59?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Modern Italian Apartment Building"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/20" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <h2 className="font-heading text-3xl font-bold mb-4">
            Inizia a gestire il tuo condominio
          </h2>
          <p className="text-lg opacity-90">
            Registrati e crea il primo condominio in pochi minuti
          </p>
        </div>
      </div>
    </div>
  );
};